import React from 'react'
import SellableCard from '#src/components/SellableCard'
import { RecommendedListSellableCardFragment } from '#src/generated/graphql'
import SliderWithScrollLock, {
  Settings as SliderProps,
  CustomArrowProps
} from '#src/components/SliderWithScrollLock'
import useBreakpoint from '#src/hooks/useBreakpoint'
import ChevronLeftIcon from './chevron-left.svg'
import ChevronRightIcon from './chevron-right.svg'
import classnames from 'classnames'
import styles from './styles.css'

interface ArrowProps extends CustomArrowProps {
  gutterSize?: number
}

const PrevArrow: React.SFC<ArrowProps> = ({ onClick, className }) => {
  return (
    <div className={classnames(styles.prevArrow, className)} onClick={onClick}>
      <ChevronLeftIcon className={styles.chevronIcon} />
    </div>
  )
}

const NextArrow: React.SFC<ArrowProps> = ({ gutterSize, onClick, className }) => {
  return (
    <div
      className={classnames(styles.nextArrow, className)}
      style={{ marginRight: gutterSize && `${gutterSize}px` }}
      onClick={onClick}
    >
      <ChevronRightIcon className={styles.chevronIcon} />
    </div>
  )
}

export interface SellableCardCarouselProps extends SliderProps {
  sellables: RecommendedListSellableCardFragment[] | null
  slidesToShow?: number
  gutterSize?: number
  aspectRatio?: 'tall' | 'wide'
  onCardClicked?: (idx: number) => void
}

const SellableCardCarousel: React.SFC<SellableCardCarouselProps> = ({
  sellables,
  slidesToShow,
  aspectRatio = 'tall',
  gutterSize = 16,
  onCardClicked,
  ...rest
}) => {
  const isLargerThanSm = useBreakpoint('sm')
  const isLargerThanMd = useBreakpoint('md')
  const isLargerThanLg = useBreakpoint('lg')
  const isLargerThanXlg = useBreakpoint('xlg')

  // TODO: Add gutter sizes to breakpoints.css (like @deal/components)
  let actualGutterSize = gutterSize
  if (isLargerThanLg) {
    actualGutterSize = 24
  }

  // Allow an override to be passed in
  let numberOfSlides

  if (slidesToShow) {
    numberOfSlides = isLargerThanSm ? slidesToShow : 1
  } else {
    // On mobile and tablet devices we use an uneven number of cards to "tease" the next slide
    numberOfSlides = aspectRatio === 'wide' ? 1.15 : 2.15

    if (aspectRatio === 'wide') {
      if (isLargerThanSm) {
        numberOfSlides = 8 / 3 // 8 columns, 3 columns per slide
      }
      if (isLargerThanMd) {
        numberOfSlides = 9 / 3 // 9 columns, 3 columns per slide
      }
    } else {
      if (isLargerThanSm) {
        numberOfSlides = 8 / 3 // 8 columns, 3 columns per slide
      }
      if (isLargerThanMd) {
        numberOfSlides = 9 / 3 // 9 columns, 3 columns per slide
      }
      if (isLargerThanXlg) {
        numberOfSlides = 10 / 2 // 10 columns, 2 columns per slide
      }
    }
  }

  return (
    <div className={styles.slider} style={{ marginRight: `${-1 * gutterSize}px` }}>
      <SliderWithScrollLock
        nextArrow={<NextArrow gutterSize={actualGutterSize} />}
        prevArrow={<PrevArrow gutterSize={actualGutterSize} />}
        slidesToShow={numberOfSlides}
        slidesToScroll={Math.floor(numberOfSlides)}
        {...rest}
      >
        {sellables &&
          sellables.map((sellable, idx) => {
            return (
              // This wrapping div is necessary because react slick overrides `style`
              <div key={sellable.id}>
                <div style={{ marginRight: `${gutterSize}px` }}>
                  <SellableCard
                    sellable={sellable}
                    onClick={onCardClicked ? () => onCardClicked(idx) : undefined}
                  />
                </div>
              </div>
            )
          })}
      </SliderWithScrollLock>
    </div>
  )
}

export default SellableCardCarousel
